<template>
  <v-list color="primary" density="compact" nav :prepend-gap="16">
    <v-list-item
      ref="supportButton"
      v-tooltip:end="{ text: t('support.button'), disabled: !smallNav }"
      base-color="warn"
      prepend-icon="mdi-message-question-outline"
      :title="t('support.button')"
      variant="tonal"
      data-testid="sidemenu-support"
      @click="supportDialog = true"
    />
    <v-menu location="end bottom">
      <template #activator="{ props: userMenuProps }">
        <v-list-item
          ref="userMenuButton"
          v-tooltip:end="{
            text: user.authenticatedUser?.firebaseUser.email,
            disabled: !smallNav,
          }"
          v-bind="userMenuProps"
          prepend-icon="mdi-account-circle-outline"
          data-testid="sidemenu-user"
          :title="user.authenticatedUser?.firebaseUser.email ?? undefined"
        >
          <template v-if="!smallNav" #append>
            <v-icon icon="mdi-dots-vertical" size="small" />
          </template>
        </v-list-item>
      </template>
      <v-defaults-provider :defaults="{ VIcon: { size: 20 } }">
        <v-list color="primary" density="compact" nav :prepend-gap="16">
          <v-list-item
            prepend-icon="mdi-cog-outline"
            :title="t('sidemenu.settings')"
            data-testid="sidemenu-settings"
            @click="myAccountOpen = true"
          />
          <v-list-item
            base-color="error"
            prepend-icon="mdi-logout mdi-flip-h"
            :title="t('sidemenu.logout')"
            data-testid="sidemenu-logout"
            @click="logout"
          />
        </v-list>
      </v-defaults-provider>
    </v-menu>
    <v-list-item
      v-if="smallNav"
      v-tooltip:end="{ text: t('sidemenu.expand') }"
      prepend-icon="mdi-arrow-expand-right"
      :title="t('sidemenu.expand')"
      @click="smallNav = !smallNav"
    />
  </v-list>
  <v-dialog v-model="supportDialog" width="560" persistent aria-labelledby="support-dialog-title">
    <v-card>
      <v-card-title class="flex items-center">
        <span id="support-dialog-title">{{ t("support.title") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          :aria-label="t('common.close')"
          variant="text"
          size="small"
          :disabled="supportLoading"
          @click="supportDialog = false"
        />
      </v-card-title>
      <v-card-text>
        <v-form
          id="support-form"
          ref="supportForm"
          class="flex flex-col gap-4"
          :disabled="supportLoading"
          @submit.prevent="sendSupportMessage"
        >
          <p>{{ t("support.description") }}</p>
          <v-textarea
            v-model="supportMessage"
            :label="t('support.message')"
            :rules="[required]"
            auto-grow
            autofocus
            counter="5000"
            maxlength="5000"
            rows="5"
            variant="outlined"
          />
          <p class="text-sm text-medium-emphasis -mt-3">
            {{ t("support.preferEmail") }}
            <a :href="`mailto:${SUPPORT_EMAIL}`">{{ SUPPORT_EMAIL }}</a>
          </p>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn :disabled="supportLoading" @click="supportDialog = false">{{
          t("common.close")
        }}</v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          type="submit"
          form="support-form"
          color="primary"
          :loading="supportLoading"
          :disabled="supportLoading"
          prepend-icon="mdi-send"
          >{{ t("common.send") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
  <MyAccount v-model="myAccountOpen" />
  <v-list v-if="smallNav" nav>
    <v-list-item href="https://aircap.tech" target="_blank" class="px-1 pt-2">
      <AircapLogo size="30px" />
    </v-list-item>
  </v-list>
  <v-list v-else nav>
    <v-list-item class="px-1 pt-2">
      <div class="flex items-center">
        <a href="https://aircap.tech" target="_blank" class="flex shrink-0">
          <AircapLogotype size="30px" />
        </a>
        <div class="ml-auto flex items-center gap-4">
          <v-btn
            v-tooltip:top="{ text: t('sidemenu.theme') }"
            color="grey"
            density="compact"
            icon
            size="small"
            variant="text"
            @click="toggleTheme"
          >
            <v-icon :icon="themeIcon" />
          </v-btn>
          <v-menu location="end">
            <template #activator="{ props: languageProps }">
              <v-btn
                v-tooltip:top="{ text: t('sidemenu.language') }"
                v-bind="languageProps"
                color="grey"
                density="compact"
                icon
                size="small"
                variant="text"
              >
                <v-icon icon="mdi-web" />
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="lang in languages"
                :key="lang.code"
                :active="currentLanguage.code === lang.code"
                :title="lang.name"
                @click="changeLanguage(lang.code)"
              />
            </v-list>
          </v-menu>
          <v-btn
            v-tooltip:top="{ text: t('sidemenu.collapse') }"
            color="grey"
            density="compact"
            icon
            size="small"
            variant="text"
            @click="smallNav = !smallNav"
          >
            <v-icon icon="mdi-arrow-collapse-left" />
          </v-btn>
        </div>
      </div>
    </v-list-item>
  </v-list>
</template>

<script lang="ts" setup>
import AircapLogo from "@/components/AircapLogo.vue";
import AircapLogotype from "@/components/AircapLogotype.vue";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { useThemePreference } from "@/composables/useThemePreference";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { useUserStore } from "@/store/user";
import MyAccount from "@/views/MyAccount/MyAccount.vue";
import { SUPPORT_EMAIL } from "@aircap/common/config";
import { captureException } from "@sentry/vue";
import { useStorage } from "@vueuse/core";
import { nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useFirebaseAuth } from "vuefire";
import type { VForm } from "vuetify/components";

const { t } = useI18n();
const { required } = useRules();
const route = useRoute();
const user = useUserStore();
const { showSuccessToast, showErrorToast } = useToast();
const { languages, changeLanguage, currentLanguage } = useLanguage();
const { icon: themeIcon, toggleTheme } = useThemePreference();
const auth = useFirebaseAuth();
const smallNav = useStorage("use-small-nav", false);

const supportDialog = ref(false);
const supportButton = ref<{ $el?: HTMLElement }>();
const supportForm = ref<VForm>();
const supportMessage = ref("");
const supportLoading = ref(false);
const myAccountOpen = ref(false);
const userMenuButton = ref<{ $el?: HTMLElement }>();

watch(supportDialog, (isOpen) => {
  if (isOpen) return;
  void nextTick(() => supportButton.value?.$el?.focus());
});

watch(myAccountOpen, (isOpen) => {
  if (isOpen) return;
  void nextTick(() => userMenuButton.value?.$el?.focus());
});

const sendSupportMessage = async () => {
  const validation = await supportForm.value?.validate();
  if (!validation?.valid) return;
  supportLoading.value = true;
  try {
    await call("sendSupportMessage")({ message: supportMessage.value, path: route.fullPath });
    supportDialog.value = false;
    supportMessage.value = "";
    showSuccessToast(t("support.sent"));
  } catch (cause) {
    const error = new Error("Error sending support message", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    supportLoading.value = false;
  }
};

const logout = async () => {
  try {
    await auth?.signOut();
  } catch (cause) {
    const error = new Error("Error signing out", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};
</script>
